import { useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import type { Transaction } from '../../lib/api';
import { formatAmount, calculateDisplayRate, formatDisplayRate } from '../../lib/format';
import {
  TX_TYPE_LABELS,
  TX_TYPE_COLORS,
  exchangeSubtype,
} from './constants';
import {
  fmtDate,
  fmtDateMadrid,
  fmtTimeLocal,
  fmtTimeMadrid,
} from './dateHelpers';

// ─── Cells ────────────────────────────────────────────────────────────────────

function Th({
  children,
  right,
  color,
}: {
  children: ReactNode;
  right?: boolean;
  color?: 'teal' | 'red';
}) {
  return (
    <th
      style={{ fontSize: '11px' }}
      className={`px-2 py-2 font-semibold uppercase tracking-wide whitespace-nowrap ${
        right ? 'text-right' : 'text-left'
      } ${
        color === 'teal'
          ? 'text-ctp-teal'
          : color === 'red'
            ? 'text-ctp-red'
            : 'text-foreground-muted'
      }`}
    >
      {children}
    </th>
  );
}

function fmtNum(n: number, currency: string): string {
  return formatAmount(n.toFixed(8), currency);
}

function AmountTotal({ amount, currency }: { amount: number | null; currency: string | null }) {
  if (amount === null) return <td className="px-2 py-1.5 text-right text-foreground-muted">–</td>;
  return (
    <td className="px-2 py-1.5 text-right">
      <span className="font-mono text-foreground-muted">{fmtNum(amount, currency ?? '')}</span>
      <span className="ml-1 text-[11px] text-foreground-muted opacity-70">{currency}</span>
    </td>
  );
}

function AmountCurrent({
  amount,
  currency,
  debt,
  status,
  color,
}: {
  amount: number | null;
  currency: string | null;
  debt: number;
  status: string;
  color: 'teal' | 'red';
}) {
  if (amount === null) return <td className="px-2 py-1.5 text-right text-foreground-muted">–</td>;
  const current = status === 'closed' ? amount : Math.max(0, amount - debt);
  const hasDebt = status === 'open' && Math.abs(current - amount) > 0.0001;
  const cls = hasDebt ? (color === 'teal' ? 'text-ctp-teal' : 'text-ctp-red') : 'text-foreground';
  return (
    <td className="px-2 py-1.5 text-right">
      <span className={`font-mono font-bold ${cls}`}>{fmtNum(current, currency ?? '')}</span>
      <span className={`ml-1 text-[11px] ${hasDebt ? cls : 'text-foreground-muted opacity-70'}`}>{currency}</span>
      {hasDebt && <span className={`ml-1 text-[10px] ${cls} opacity-70`}>●</span>}
    </td>
  );
}

// ─── Row ──────────────────────────────────────────────────────────────────────

function TxRow({ tx, onClick }: { tx: Transaction; onClick: () => void }) {
  const { t } = useTranslation('transactions');
  const isExchange = tx.type === 'exchange';
  const isDeposit = tx.type === 'deposit';
  const isTransfer = tx.type === 'transfer';
  const showClosedPeriod = tx.status === 'closed' && tx.inClosedPeriod === true;

  let amtIn: number | null = null;
  let curIn: string | null = null;
  let amtOut: number | null = null;
  let curOut: string | null = null;

  if (isExchange || isTransfer) {
    amtIn = parseFloat(String(tx.amountIn || 0));
    curIn = tx.currencyIn || null;
    amtOut = parseFloat(String(tx.amountOut || 0));
    curOut = tx.currencyOut || null;
  } else if (isDeposit) {
    amtIn = parseFloat(String(tx.amount || 0));
    curIn = tx.currency || null;
  } else {
    amtOut = parseFloat(String(tx.amount || 0));
    curOut = tx.currency || null;
  }

  const debtIn = parseFloat(String(tx.debtAmountIn || '0'));
  const debtOut = parseFloat(String(tx.debtAmountOut || '0'));
  const rate = calculateDisplayRate(tx);
  const sub = isExchange ? exchangeSubtype(tx.currencyIn) : null;
  const td = 'px-2 py-1.5 whitespace-nowrap';

  return (
    <tr className="hover:bg-surface-hover transition-colors cursor-pointer" onClick={onClick}>
      <td className={td}>
        <span className="font-mono text-ctp-blue">{tx.id.slice(0, 8)}</span>
      </td>
      <td className={`${td} max-w-[150px]`}>
        <span className="text-foreground-muted truncate block">{tx.comment || '–'}</span>
      </td>
      <td className={td}>
        <span className="font-mono text-foreground">{fmtDate(tx.createdAt)}</span>
      </td>
      <td className={td}>
        <span className="font-mono text-foreground-muted">{fmtDateMadrid(tx.createdAt)}</span>
      </td>
      <td className={td}>
        <span className="font-mono text-foreground-muted">
          {tx.transactionDate ? fmtDate(tx.transactionDate) : '–'}
        </span>
      </td>
      <td className={td}>
        <span className="font-mono text-foreground">{fmtTimeLocal(tx.createdAt)}</span>
      </td>
      <td className={td}>
        <span className="font-mono text-foreground-muted">{fmtTimeMadrid(tx.createdAt)}</span>
      </td>
      <td className={td}>
        <span className="font-medium text-foreground">{tx.clientName || '–'}</span>
      </td>
      <td className={td}>
        <span
          className={`px-1.5 py-0.5 rounded text-[11px] font-bold uppercase tracking-wide ${
            TX_TYPE_COLORS[tx.type] ?? 'bg-surface-hover text-foreground-muted'
          }`}
        >
          {TX_TYPE_LABELS[tx.type] ?? tx.type}
          {sub && (
            <span
              className={`ml-1 normal-case font-normal ${
                sub === 'продажа' ? 'text-ctp-red' : 'opacity-75'
              }`}
            >
              ({sub})
            </span>
          )}
        </span>
      </td>
      <AmountTotal amount={amtIn} currency={curIn} />
      <AmountCurrent amount={amtIn} currency={curIn} debt={debtIn} status={tx.status} color="teal" />
      <AmountTotal amount={amtOut} currency={curOut} />
      <AmountCurrent amount={amtOut} currency={curOut} debt={debtOut} status={tx.status} color="red" />
      <td className={`${td} text-right`}>
        <span className="font-mono text-foreground-muted text-[11px]">{formatDisplayRate(rate)}</span>
      </td>
      <td className={`${td} text-right`}>
        <span className="font-mono text-foreground-muted">
          {tx.amount ? formatAmount(tx.amount, tx.currency ?? '') : '–'}
        </span>
        {tx.amount && tx.currency && (
          <span className="ml-1 text-[11px] text-foreground-muted opacity-70">{tx.currency}</span>
        )}
      </td>
      <td className={`${td} text-right`}>
        <span className="font-mono text-foreground-muted">
          {tx.debtAmountIn ? formatAmount(tx.debtAmountIn, curIn ?? '') : '–'}
        </span>
      </td>
      <td className={`${td} text-right`}>
        <span className="font-mono text-foreground-muted">
          {tx.debtAmountOut ? formatAmount(tx.debtAmountOut, curOut ?? '') : '–'}
        </span>
      </td>
      <td className={`${td} text-right`}>
        <span className="font-mono text-foreground-muted">
          {tx.originalDebtAmountIn ? formatAmount(tx.originalDebtAmountIn, curIn ?? '') : '–'}
        </span>
      </td>
      <td className={`${td} text-right`}>
        <span className="font-mono text-foreground-muted">
          {tx.originalDebtAmountOut ? formatAmount(tx.originalDebtAmountOut, curOut ?? '') : '–'}
        </span>
      </td>
      <td className={td}>
        <div className="inline-flex items-center gap-1.5">
          <span
            className={`inline-flex items-center gap-1 font-semibold px-1.5 py-0.5 rounded-full ${
              tx.status === 'closed'
                ? 'bg-surface-hover text-foreground-muted'
                : 'bg-ctp-peach/15 text-ctp-peach'
            }`}
            style={{ fontSize: '11px' }}
          >
            <span style={{ fontSize: '8px' }}>●</span>
            {tx.status === 'closed' ? 'Закрыта' : 'Открыта'}
          </span>
          {showClosedPeriod && (
            <span
              className="inline-flex items-center text-ctp-yellow"
              title={t('closedPeriod.indicator')}
              aria-label={t('closedPeriod.indicator')}
            >
              <Lock size={12} />
            </span>
          )}
        </div>
      </td>
      <td className={td}>
        <span className="text-foreground-muted text-[11px]">{tx.employeeName || '–'}</span>
      </td>
    </tr>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export interface TransactionTableProps {
  transactions: Transaction[];
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  /** Custom empty state JSX. If absent, renders a plain "Нет транзакций" row. */
  emptyState?: ReactNode;
}

const COLUMN_COUNT = 21;

export function TransactionTable({
  transactions,
  loading,
  error,
  onRetry,
  emptyState,
}: TransactionTableProps) {
  const navigate = useNavigate();

  if (loading) {
    return <div className="py-12 text-center text-foreground-muted text-sm">Загрузка...</div>;
  }

  if (error) {
    return (
      <div className="py-12 text-center text-ctp-red text-sm flex flex-col items-center gap-3">
        {error}
        {onRetry && (
          <button
            onClick={onRetry}
            className="px-4 py-2 bg-ctp-teal text-white rounded-lg text-sm hover:opacity-90"
          >
            Повторить
          </button>
        )}
      </div>
    );
  }

  if (transactions.length === 0 && emptyState) {
    return <>{emptyState}</>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left border-collapse" style={{ fontSize: '12px' }}>
        <thead>
          <tr className="bg-surface-hover border-b border-border">
            <Th>ID</Th>
            <Th>Комментарий</Th>
            <Th>Дата IN (лок.)</Th>
            <Th>Дата IN (Mad.)</Th>
            <Th>Дата OUT</Th>
            <Th>Время (лок.)</Th>
            <Th>Время (Mad.)</Th>
            <Th>Клиент</Th>
            <Th>Тип</Th>
            <Th right>Приход (всего)</Th>
            <Th right color="teal">Приход (тек.)</Th>
            <Th right>Расход (всего)</Th>
            <Th right color="red">Расход (тек.)</Th>
            <Th right>Курс</Th>
            <Th right>Сумма</Th>
            <Th right>Долг IN</Th>
            <Th right>Долг OUT</Th>
            <Th right>Ориг. долг IN</Th>
            <Th right>Ориг. долг OUT</Th>
            <Th>Статус</Th>
            <Th>Кассир</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {transactions.length === 0 ? (
            <tr>
              <td colSpan={COLUMN_COUNT} className="py-12 text-center text-foreground-muted">
                Нет транзакций
              </td>
            </tr>
          ) : (
            transactions.map(tx => (
              <TxRow key={tx.id} tx={tx} onClick={() => navigate(`/transactions/${tx.id}`)} />
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export default TransactionTable;
